import { OnboardingSaveParkingDto, DayOfWeek } from '@interfaces/models';
import { CityDto, ZoneDto } from '@interfaces/types';
import { useEffect, useState } from 'react';

import { useServicesFormatter } from '../hooks';

export const useParkingConfiguration = ({
  initialConfiguration,
  defaultSelected,
  onUpdate,
  cities,
  zones,
  days,
}: {
  onUpdate: (config: OnboardingSaveParkingDto | null) => void;
  initialConfiguration?: OnboardingSaveParkingDto | null;
  defaultSelected?: boolean;
  cities: CityDto[];
  days: DayOfWeek[];
  zones: ZoneDto[];
}) => {
  const [isEntityActive, setIsEntityActive] = useState(!!initialConfiguration);
  const [isEditing, setIsEditing] = useState(false);

  const [configuration, setConfiguration] = useState<OnboardingSaveParkingDto | null>(
    initialConfiguration || null,
  );

  useEffect(() => {
    setConfiguration(initialConfiguration || null);
  }, [initialConfiguration]);

  useEffect(() => {
    if (defaultSelected) handleActiveChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected]);

  const { formatCities, formatDays, formatTime, formatZones } = useServicesFormatter();

  const prepareParkingPreviewData = (parkingConfiguration: OnboardingSaveParkingDto) => {
    return [
      {
        key: 'COMPANY_POLICY.PARKING_CONFIGURATION_CITIES',
        value: formatCities(parkingConfiguration.cities, cities),
      },
      {
        key: 'COMPANY_POLICY.PARKING_CONFIGURATION_ZONES',
        value: formatZones(parkingConfiguration.cities, parkingConfiguration.zones, cities, zones),
      },
      {
        key: 'COMPANY_POLICY.PARKING_CONFIGURATION_DAYS',
        value: formatDays(parkingConfiguration.days as DayOfWeek[]),
      },
      {
        key: 'COMPANY_POLICY.PARKING_CONFIGURATION_TIME',
        value: formatTime(
          parkingConfiguration.unlimitedTime,
          parkingConfiguration.timeFrom ?? '',
          parkingConfiguration.timeTo ?? '',
        ),
      },
    ];
  };

  const createDefaultConfiguration = (): OnboardingSaveParkingDto => ({
    cities: cities.map((city) => city.code),
    zones: zones.map((zone) => zone.code),
    days: days.slice(0, 5),
    unlimitedTime: true,
    timeFrom: '08',
    timeTo: '20',
  });

  const handleActiveChange = (active: boolean) => {
    if (active) {
      const defaultConfig = createDefaultConfiguration();
      setIsEntityActive(true);
      setConfiguration(defaultConfig);
      onUpdate(defaultConfig);
    } else {
      setIsEntityActive(false);
      setConfiguration(null);
      setIsEditing(false);
      onUpdate(null);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = (newConfig: OnboardingSaveParkingDto) => {
    setConfiguration(newConfig);
    setIsEditing(false);
    onUpdate(newConfig);
  };

  const handleRevertChanges = () => {
    const defaultConfig = createDefaultConfiguration();
    setConfiguration(defaultConfig);
    setIsEditing(false);
    onUpdate(defaultConfig);
  };

  return {
    isEntityActive,
    isEditing,
    configuration,
    prepareParkingPreviewData,
    handleActiveChange,
    handleEdit,
    handleSave,
    handleRevertChanges,
    createDefaultConfiguration,
  };
};
